import { useCallback, useMemo } from 'react'

import { F0Box } from '@factorialco/f0-react'
import {
  ParticipantsNamesDocument,
  type PlanningValueObjectsParticipantRoleEnum,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

import ParticipantLoadingList from './ParticipantLoadingList'
import ParticipantRow, { type EmployeeInfo, type SelectedParticipant } from './ParticipantRow'

const VIEWER_ROLE: PlanningValueObjectsParticipantRoleEnum = 'viewer'

interface ParticipantSelectorProps {
  selectedParticipants: SelectedParticipant[]
  onParticipantsChange: (participants: SelectedParticipant[]) => void
}

const ParticipantSelector = ({
  selectedParticipants,
  onParticipantsChange,
}: ParticipantSelectorProps) => {
  const selectedEmployeeIds = useMemo(
    () => selectedParticipants.map((c) => c.employeeId),
    [selectedParticipants]
  )

  const { data: employeesData, loading: loadingNames } = useQuery(ParticipantsNamesDocument, {
    variables: { ids: selectedEmployeeIds },
    skip: selectedEmployeeIds.length === 0,
  })

  const employeeMap = useMemo(() => {
    const map = new Map<string, EmployeeInfo>()
    const nodes = employeesData?.employees?.employeeNamesConnection?.nodes ?? []
    for (const e of nodes) {
      map.set(e.id, {
        firstName: e.firstName,
        lastName: e.lastName,
        avatarUrl: buildUrl(e.avatar?.url),
      })
    }
    return map
  }, [employeesData])

  const handleEmployeeChange = useCallback(
    (state: EmployeeSelectorV2MultiSelectState) => {
      if (state.type !== 'manual') return

      const newIds = state.ids
      const kept = selectedParticipants.filter((c) => newIds.includes(c.employeeId))
      const added = newIds
        .filter((id) => !selectedParticipants.some((c) => c.employeeId === id))
        .map((id) => ({ employeeId: id, role: VIEWER_ROLE }))
      onParticipantsChange([...kept, ...added])
    },
    [selectedParticipants, onParticipantsChange]
  )

  const handleRoleChange = useCallback(
    (employeeId: string, role: PlanningValueObjectsParticipantRoleEnum) => {
      onParticipantsChange(
        selectedParticipants.map((c) => (c.employeeId === employeeId ? { ...c, role } : c))
      )
    },
    [selectedParticipants, onParticipantsChange]
  )

  return (
    <F0Box gap="lg" display="flex" flexDirection="column">
      <EmployeeSelectorV2
        multiple
        value={selectedEmployeeIds}
        onChange={handleEmployeeChange}
        label={i18n.t('planning.manage_collaborators.select_label')}
        options={{ disableSelectAll: true }}
      />
      {selectedParticipants.length > 0 && (
        <F0Box gap="lg" display="flex" flexDirection="column">
          {loadingNames ? (
            <ParticipantLoadingList count={selectedParticipants.length} />
          ) : (
            selectedParticipants.map((participant) => (
              <ParticipantRow
                key={participant.employeeId}
                participant={participant}
                employee={employeeMap.get(participant.employeeId)}
                onRoleChange={handleRoleChange}
              />
            ))
          )}
        </F0Box>
      )}
    </F0Box>
  )
}

export default ParticipantSelector
